import { AgencyId } from "@inrev/common";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import { IslandLayout } from "@portal/components/layout/IslandLayout";
import { Button } from "@portal/components/ui/Button";
import { Card } from "@portal/components/ui/Card";
import { getStatusTagByStatus } from "@portal/components/ui/StatusTag";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { agentUserRoleTypeLabelMap } from "../../../constants";
import { formatEmpty } from "../../../utils";
import { AgentInviteFormModal } from "../../shared/settings/AgentInviteFormModal";
import { InvitationPending } from "../../shared/settings/InvitationPending";
import { AdminUserMenu } from "./AdminUserMenu";

export const AdminAgencyView = () => {
	const { id } = useParams();
	const [inviteFormOpen, setInviteFormOpen] = useState(false);
	const { agencyNameMap, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();
	const { agentPreviews, agentPreviewsLoading } = useAdminFetchAgentPreviewsForAgency(
		id as unknown as AgencyId,
		true,
	);

	const agencyName = agencyNameMap[id as unknown as AgencyId] || "";

	return agentPreviewsLoading || agencyPreviewsLoading ? (
		<div>Loading...</div>
	) : (
		<IslandLayout userMenu={<AdminUserMenu />}>
			<div className="flex flex-col gap-[30px] w-[1000px] max-w-[1000px] h-fit flex-flex-col mt-[25px] mx-[40px] pb-[75px]">
				<div className="flex w-full justify-between gap-[25px]">
					<span
						data-testid="admin-agency-view-title"
						className="text-[26px] text-gray-800 font-semibold"
					>
						{agencyName || "Agency"}
					</span>
					<Button
						testId="invite-agent-button"
						color="primary"
						thinFont
						onClick={() => setInviteFormOpen(true)}
						disabled={!agentPreviews || agentPreviewsLoading}
					>
						Invite an Agent
					</Button>
				</div>
				<div className="w-full flex flex-col">
					<Card className="flex items-center bg-surface-neutral w-full gap-[12px] px-[12px] py-[5px] border-gray-400 text-[14px] font-medium text-secondary">
						<div data-testid="email-header" className="flex-[3] truncate">
							Email
						</div>
						<div data-testid="first-name-header" className="flex-[2] truncate">
							First Name
						</div>
						<div data-testid="last-name-header" className="flex-[2] truncate">
							Last Name
						</div>
						<div data-testid="role-header" className="flex-[2] truncate">
							Role
						</div>
						<div data-testid="status-header" className="flex-[2] truncate">
							Status
						</div>
					</Card>
					{agentPreviews && !agentPreviewsLoading && (
						<div
							data-testid="agent-list"
							className="w-full relative h-fit flex flex-col text-primary"
						>
							{agentPreviews.length > 0 ? (
								agentPreviews
									.sort((a, b) => Number(a.invitationPending) - Number(b.invitationPending))
									.map((agentPreview) => (
										<div
											key={agentPreview.id}
											data-testid="agent-row"
											className="flex items-center gap-[12px] w-full px-[12px] py-[18px] border-b border-b-gray-200/80"
										>
											<div className="flex-[3] truncate">{agentPreview.email}</div>
											<div className="flex-[2] truncate">{agentPreview.firstName}</div>
											<div className="flex-[2] truncate">{agentPreview.lastName}</div>
											<div className="flex-[2] truncate">
												{formatEmpty(agentUserRoleTypeLabelMap[agentPreview.roles[0]])}
											</div>
											<div className="flex-[2] truncate">
												{agentPreview.invitationPending && (
													<InvitationPending agent={agentPreview} agencyId={id as AgencyId} />
												)}
												{!agentPreview.invitationPending && getStatusTagByStatus("active")}
											</div>
										</div>
									))
							) : (
								<div
									data-testid="empty-agent-label"
									className="w-full px-[12px] py-[18px] pt-8 text-center text-tertiary italic"
								>
									No agents found
								</div>
							)}
						</div>
					)}
				</div>
			</div>
			{inviteFormOpen && (
				<AgentInviteFormModal onClose={() => setInviteFormOpen(false)} agencyId={id as AgencyId} />
			)}
		</IslandLayout>
	);
};
